import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './RestaurantSelector.css';
import { StoreContext } from '../../Context/StoreContext';
import ImageComponent from '../common/Image';
import RestaurantShimmer from '../common/RestaurantShimmer';
import { calculateDistance } from '../../utils/locationUtils';
import { useTranslation } from 'react-i18next';

// Utility function to ensure consistent string keys for React
const getStringKey = (id, fallback = '') => {
    if (!id) return fallback;
    if (typeof id === 'string') return id;
    
    // Handle populated MongoDB documents
    if (typeof id === 'object' && id._id) {
        return id._id.toString();
    }
    
    if (id.toString && typeof id.toString === 'function') return id.toString();
    return String(id) || fallback;
};

const RestaurantSelector = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { url, setSelectedRestaurant, selectedRestaurant, formatImageUrl, userAddress, restaurant_list, calculateDeliveryFee, currency } = useContext(StoreContext);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredRestaurants, setFilteredRestaurants] = useState([]);
  const [cuisineFilter, setCuisineFilter] = useState('');
  const [priceFilter, setPriceFilter] = useState('');
  const [availableCuisines, setAvailableCuisines] = useState([]);
  const [availablePriceRanges, setAvailablePriceRanges] = useState([]);
  const [availableRatings, setAvailableRatings] = useState([]);
  const [ratingFilter, setRatingFilter] = useState(''); // Rating filter: '4.0+', '4.5+', etc.
  const [timeFilter, setTimeFilter] = useState(false); // Under 40 min filter
  const [statusFilter, setStatusFilter] = useState(''); // Open Now or empty
  const [deliveryFeeFilter, setDeliveryFeeFilter] = useState(''); // Free, Under 3000, etc.
  const [activeDropdown, setActiveDropdown] = useState(null); // Track which dropdown is open
  const [currentTime, setCurrentTime] = useState(new Date()); // For real-time status updates
  const [isMobile, setIsMobile] = useState(false); // Track mobile screen size
  const [estimatedDeliveryFees, setEstimatedDeliveryFees] = useState({}); // Store delivery fees for each restaurant
  const [isCalculatingFees, setIsCalculatingFees] = useState(false); // Track fee calculation state

  // Calculate distance between user and restaurant
  const calculateRestaurantDistance = (restaurant) => {
    if (!userAddress?.coordinates) {
      return null;
    }
    
    const restaurantCoords = restaurant?.address?.coordinates || restaurant?.address?.location;
    
    if (!restaurantCoords) {
      return null;
    }
    
    const userLat = userAddress.coordinates.latitude;
    const userLng = userAddress.coordinates.longitude;
    
    let restLat, restLng;
    
    if (restaurantCoords.type === 'Point' && Array.isArray(restaurantCoords.coordinates)) {
      restLng = restaurantCoords.coordinates[0];
      restLat = restaurantCoords.coordinates[1];
    } else {
      restLat = restaurantCoords.latitude || restaurantCoords.lat;
      restLng = restaurantCoords.longitude || restaurantCoords.lng || restaurantCoords.lon;
    }
    
    if (!userLat || !userLng || !restLat || !restLng) {
      return null;
    }
    
    try {
      const distance = calculateDistance(userLat, userLng, restLat, restLng);
      return distance;
    } catch (error) {
      return null;
    }
  };

  // Get distance and estimated delivery time
  const getDeliveryInfo = (restaurant) => {
    const distance = calculateRestaurantDistance(restaurant);
    if (distance !== null) {
      // Estimate delivery time based on distance (rough calculation)
      const estimatedTime = Math.max(15, Math.round(distance * 8 + 10)); // 8 min per km + 10 min prep
      return {
        distance: `${distance.toFixed(1)} ${t('restaurants.filters.km_away')}`,
        time: `${estimatedTime} min`
      };
    }
    return {
      distance: 'Distance N/A',
      time: '-- min'
    };
  };

  // Calculate delivery fees for all restaurants when user address changes
  const calculateDeliveryFeesForAllRestaurants = async () => {
    if (!userAddress?.coordinates || !restaurants.length) {
      setEstimatedDeliveryFees({});
      return;
    }

    // Don't recalculate if we already have fees for all restaurants
    const hasAllFees = restaurants.every(restaurant => estimatedDeliveryFees[restaurant._id] !== undefined);
    if (hasAllFees && Object.keys(estimatedDeliveryFees).length > 0) {
      return;
    }

    setIsCalculatingFees(true);
    const fees = {};

    try {
      // Calculate fees for all restaurants in parallel
      const feePromises = restaurants.map(async (restaurant) => {
        // Skip if we already have a fee for this restaurant
        if (estimatedDeliveryFees[restaurant._id] !== undefined) {
          return { restaurantId: restaurant._id, fee: estimatedDeliveryFees[restaurant._id] };
        }

        try {
          const customerAddress = {
            coordinates: userAddress.coordinates
          };

          const fee = await calculateDeliveryFee(restaurant._id, customerAddress);
          return { restaurantId: restaurant._id, fee };
        } catch (error) {
          console.error(`Error calculating delivery fee for restaurant ${restaurant._id}:`, error);
          return { restaurantId: restaurant._id, fee: 1000 }; // Default fee
        }
      });

      const results = await Promise.all(feePromises);
      
      // Convert results to object
      results.forEach(({ restaurantId, fee }) => {
        fees[restaurantId] = fee;
      });

      setEstimatedDeliveryFees(fees);
    } catch (error) {
      console.error('Error calculating delivery fees:', error);
    } finally {
      setIsCalculatingFees(false);
    }
  };

  // Calculate delivery fees when user address or restaurants change
  useEffect(() => {
    calculateDeliveryFeesForAllRestaurants();
  }, [userAddress?.coordinates, restaurants]);

  // Fetch restaurants from API
  useEffect(() => {
    const fetchRestaurants = async () => {
      try {
        setLoading(true);
        
        // First, try to use restaurants from StoreContext if available
        if (restaurant_list && restaurant_list.length > 0) {
          console.log('RestaurantSelector: Using restaurants from StoreContext');
          setRestaurants(restaurant_list);
          setFilteredRestaurants(restaurant_list);
          
          const cuisines = new Set();
          const priceRanges = new Set();
          const ratings = new Set();
          
          restaurant_list.forEach(restaurant => {
            // Collect cuisines from database (cuisineType field)
            if (restaurant.cuisineType) {
              if (Array.isArray(restaurant.cuisineType)) {
                // Handle array of cuisines
                restaurant.cuisineType.forEach(cuisine => {
                  if (cuisine && typeof cuisine === 'string' && cuisine.trim()) {
                    cuisines.add(cuisine.trim());
                  }
                });
              } else if (typeof restaurant.cuisineType === 'string' && restaurant.cuisineType.trim()) {
                // Handle single cuisine string
                cuisines.add(restaurant.cuisineType.trim());
              }
            }

            // Collect price ranges
            if (restaurant.priceRange) {
              priceRanges.add(restaurant.priceRange);
            }

            // Collect ratings (rounded to nearest 0.5)
            if (restaurant.averageRating && restaurant.averageRating > 0) {
              const roundedRating = Math.round(restaurant.averageRating * 2) / 2;
              ratings.add(`${Math.floor(roundedRating)}+`);
            } else if (restaurant.rating && restaurant.rating > 0) {
              const roundedRating = Math.round(restaurant.rating * 2) / 2;
              ratings.add(`${Math.floor(roundedRating)}+`);
            }
          });

          setAvailableCuisines(Array.from(cuisines).sort());
          setAvailablePriceRanges(Array.from(priceRanges).sort());
          setAvailableRatings(Array.from(ratings).sort());
          
          setLoading(false);
        } else {
          // If no restaurants in context, make API call
          console.log('RestaurantSelector: No restaurants in context, fetching from API');
          await new Promise(resolve => setTimeout(resolve, 500)); // 500ms delay
          
          const response = await axios.get(`${url}/api/restaurant`);
          if (response.data.success && response.data.data && response.data.data.restaurants) {
            setRestaurants(response.data.data.restaurants);
            setFilteredRestaurants(response.data.data.restaurants);
            
            const cuisines = new Set();
            const priceRanges = new Set();
            const ratings = new Set();
            
            response.data.data.restaurants.forEach(restaurant => {
              // Collect cuisines from database (cuisineType field)
              if (restaurant.cuisineType) {
                if (Array.isArray(restaurant.cuisineType)) {
                  restaurant.cuisineType.forEach(cuisine => {
                    if (cuisine && typeof cuisine === 'string' && cuisine.trim()) {
                      cuisines.add(cuisine.trim());
                    }
                  });
                } else if (typeof restaurant.cuisineType === 'string' && restaurant.cuisineType.trim()) {
                  cuisines.add(restaurant.cuisineType.trim());
                }
              }

              if (restaurant.priceRange) {
                priceRanges.add(restaurant.priceRange);
              }

              if (restaurant.averageRating && restaurant.averageRating > 0) {
                const roundedRating = Math.round(restaurant.averageRating * 2) / 2;
                ratings.add(`${Math.floor(roundedRating)}+`);
              } else if (restaurant.rating && restaurant.rating > 0) {
                const roundedRating = Math.round(restaurant.rating * 2) / 2;
                ratings.add(`${Math.floor(roundedRating)}+`);
              }
            });

            setAvailableCuisines(Array.from(cuisines).sort());
            setAvailablePriceRanges(Array.from(priceRanges).sort());
            setAvailableRatings(Array.from(ratings).sort());
          } else {
            setRestaurants([]);
            setFilteredRestaurants([]);
            setAvailableCuisines([]);
            setAvailablePriceRanges([]);
            setAvailableRatings([]);
          }
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching restaurants:', error);
        setLoading(false);
      }
    };

    fetchRestaurants();
  }, [url, restaurant_list]); // Add restaurant_list dependency

  // Update current time every minute for real-time status updates
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 300000); // Update every 5 minutes instead of 1 minute to reduce load

    return () => clearInterval(timer);
  }, []);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Initial check
    checkMobile();

    // Listen for window resize
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get restaurant operation status (real-time based on database business hours)
  const getRestaurantStatus = (restaurant) => {
    // Check for manual override first
    if (restaurant.operationalStatus?.override && restaurant.operationalStatus.override !== 'auto') {
      if (restaurant.operationalStatus.override === 'open') {
        return 'open';
      } else if (restaurant.operationalStatus.override === 'closed') {
        return 'closed';
      }
    }

    // Fall back to automatic status based on business hours
    if (!restaurant.businessHours || restaurant.businessHours.length === 0) {
      return 'closed';
    }

    const now = new Date();
    const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const currentTime = now.getHours() * 60 + now.getMinutes(); // Current time in minutes

    const todayHours = restaurant.businessHours.find(hours => hours.day === currentDay);
    
    if (!todayHours) {
      return 'closed';
    }

    // Parse open and close times
    const [openHour, openMin] = todayHours.open.split(':').map(Number);
    const [closeHour, closeMin] = todayHours.close.split(':').map(Number);
    
    const openTime = openHour * 60 + openMin;
    const closeTime = closeHour * 60 + closeMin;

    if (currentTime < openTime || currentTime > closeTime) {
      return 'closed';
    } else if (closeTime - currentTime <= 60) {
      // Closing within an hour (60 minutes)
      return 'closing-soon';
    } else {
      return 'open';
    }
  };

  // Filter restaurants (includes currentTime dependency for real-time status updates)
  useEffect(() => {
    const results = restaurants.filter(restaurant => {
      const matchesSearch = restaurant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          restaurant.description.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCuisine = cuisineFilter === '' || 
                           (restaurant.cuisineType && restaurant.cuisineType.includes(cuisineFilter));
      
      const matchesPrice = priceFilter === '' || 
                          restaurant.priceRange === priceFilter;
      
      // Rating filter - filter restaurants based on actual database ratings
      const matchesRating = !ratingFilter || (() => {
        const rating = restaurant.averageRating || restaurant.rating || 0;
        if (!rating || rating === 0) return false;
        
        switch (ratingFilter) {
          case '5+':
            return rating >= 5.0;
          case '4+':
            return rating >= 4.0;
          case '3+':
            return rating >= 3.0;
          case '2+':
            return rating >= 2.0;
          default:
            return true;
        }
      })();
      
      // Time filter (Under 40 min)
      const deliveryInfo = getDeliveryInfo(restaurant);
      const estimatedTime = parseInt(deliveryInfo.time.replace(' min', ''));
      const matchesTime = !timeFilter || estimatedTime <= 40;
      
      // Status filter (Open Now only - includes both 'open' and 'closing-soon')
      const restaurantStatus = getRestaurantStatus(restaurant);
      const matchesStatus = statusFilter === '' || 
                            (statusFilter === 'open' && (restaurantStatus === 'open' || restaurantStatus === 'closing-soon'));
      
      // Delivery fee filter
      const matchesDeliveryFee = !deliveryFeeFilter || (() => {
        const restaurantFee = estimatedDeliveryFees[restaurant._id] || 1000; // Default to 1000 if not calculated
        switch (deliveryFeeFilter) {
          case 'free': return restaurantFee === 0;
          case 'under500': return restaurantFee < 500;
          case 'under1000': return restaurantFee < 1000;
          default: return true;
        }
      })();
      
      return matchesSearch && matchesCuisine && matchesPrice && matchesRating && matchesTime && matchesStatus && matchesDeliveryFee;
    });
    
    setFilteredRestaurants(results);
  }, [searchTerm, cuisineFilter, priceFilter, ratingFilter, timeFilter, statusFilter, deliveryFeeFilter, restaurants, currentTime, estimatedDeliveryFees]);

  // Handle restaurant selection
  const handleSelectRestaurant = (restaurant) => {
    navigate(`/restaurant/${restaurant._id}`);
  };

  // Get real review count from database
  const getReviewCount = (restaurant) => {
    const count = restaurant.reviewCount || 0;
    
    if (count === 0) {
      return '0';
    }
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k+`;
    }
    return `${count}`;
  };

  // Helper function to format price range display
  const formatPriceRangeDisplay = (priceRange) => {
    if (!priceRange) return 'Not specified';
    
    // Handle specific price range formats
    switch (priceRange) {
      case 'BIF': return 'BIF';
      case 'BIF BIF': return 'BIFBIF';
      case 'BIF BIF BIF': return 'BIF BIFBIF';
      default: return priceRange;
    }
  };

  // Get restaurant image
  const getRestaurantImage = (restaurant) => {
    if (restaurant.branding && restaurant.branding.logo) {
      return restaurant.branding.logo;
    }
    if (restaurant.coverImage) {
      return restaurant.coverImage;
    }
    // Return a food placeholder or generate a color based on name
    return null;
  };

  // Get dropdown margin based on screen size
  const getDropdownMargin = () => {
    return isMobile ? '40px' : '4px';
  };

  // Handle dropdown toggle
  const toggleDropdown = (dropdownType) => {
    setActiveDropdown(activeDropdown === dropdownType ? null : dropdownType);
  };

  // Close dropdown when clicking outside
  const handleDropdownSelect = (filterType, value) => {
    switch (filterType) {
      case 'delivery':
        setDeliveryFeeFilter(value);
        break;
      case 'cuisine':
        setCuisineFilter(value);
        break;
      case 'price':
        setPriceFilter(value);
        break;
      case 'rating':
        setRatingFilter(value);
        break;
    }
    setActiveDropdown(null);
  };

  return (
    <div 
      className="restaurant-selector-modern"
      onClick={(e) => {
        // Close dropdown if clicking outside
        if (!e.target.closest('.filter-pill')) {
          setActiveDropdown(null);
        }
      }}
    >
      <div className="search-filter-bar" style={{ overflow: 'visible' }}>
        <div className="search-container">
          <div className="search-input-wrapper">
            <span className="search-icon">🔍</span>
            <input
              type="text"
                                      placeholder={t('restaurants.search_placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input-compact"
            />
          </div>
        </div>
        
        <div className="filter-pills" style={{ overflow: 'visible' }}>
     
          {/* Time Filter */}
          <div 
            className={`filter-pill time ${timeFilter ? 'active' : ''}`}
            onClick={() => setTimeFilter(!timeFilter)}
            style={{ position: 'relative' }}
          >
            <span>⏱️ {t('restaurants.filters.under_40_min')}</span>
          </div>
          
          {/* Status Filter */}
          <div 
            className={`filter-pill status ${statusFilter === 'open' ? 'active' : ''}`}
            onClick={() => setStatusFilter(statusFilter === 'open' ? '' : 'open')}
            style={{ position: 'relative' }}
          >
            <span>🟢 {t('restaurants.filters.open_now')}</span>
          </div>
                    {/* Delivery Fee Filter */}
                    <div 
            className={`filter-pill delivery-fee ${deliveryFeeFilter ? 'active' : ''} ${activeDropdown === 'delivery' ? 'dropdown-open' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              toggleDropdown('delivery');
            }}
            style={{ position: 'relative', overflow: 'visible' }}
          >
            <span>🚛 {deliveryFeeFilter ? 
              (deliveryFeeFilter === 'free' ? t('restaurants.filters.free_delivery') : 
               deliveryFeeFilter === 'under2000' ? t('restaurants.filters.under_2000_bif') : t('restaurants.filters.under_3000_bif')) : 
              t('restaurants.filters.delivery_fee')}</span>
            <span className="dropdown-arrow">▼</span>
            {activeDropdown === 'delivery' && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                background: 'white',
                border: '1px solid #ddd',
                borderRadius: '8px',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                zIndex: 999999,
                maxHeight: '200px',
                overflowY: 'auto',
                marginTop: getDropdownMargin(),
                minWidth: '150px',
                display: 'block',
                visibility: 'visible'
              }}>
                <div 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDropdownSelect('delivery', '');
                  }}
                  style={{
                    padding: '0.6rem 0.75rem',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    color: '#333',
                    borderBottom: '1px solid #f0f0f0',
                    background: 'white',
                    display: 'block'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                >
                  {t('restaurants.filters.all_delivery_fees')}
                </div>
                <div 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDropdownSelect('delivery', 'free');
                  }}
                  style={{
                    padding: '0.6rem 0.75rem',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    color: '#333',
                    borderBottom: '1px solid #f0f0f0',
                    background: 'white',
                    display: 'block'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                >
                  {t('restaurants.filters.free_delivery')}
                </div>
                <div 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDropdownSelect('delivery', 'under500');
                  }}
                  style={{
                    padding: '0.6rem 0.75rem',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    color: '#333',
                    borderBottom: '1px solid #f0f0f0',
                    background: 'white',
                    display: 'block'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                >
                  {t('restaurants.filters.under_2000_bif')}
                </div>
                <div 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDropdownSelect('delivery', 'under1000');
                  }}
                  style={{
                    padding: '0.6rem 0.75rem',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    color: '#333',
                    background: 'white',
                    display: 'block'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                >
                  {t('restaurants.filters.under_3000_bif')}
                </div>
              </div>
            )}
          </div>
          
          {/* Cuisine Filter */}
          <div 
            className={`filter-pill cuisine ${cuisineFilter ? 'active' : ''} ${activeDropdown === 'cuisine' ? 'dropdown-open' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              toggleDropdown('cuisine');
            }}
            style={{ position: 'relative', overflow: 'visible' }}
            id="cuisine-filter-pill"
          >
            <span>🍴 {cuisineFilter ? (cuisineFilter.charAt(0).toUpperCase() + cuisineFilter.slice(1)) : t('restaurants.filters.all_cuisines')}</span>
            <span className="dropdown-arrow">▼</span>
            {activeDropdown === 'cuisine' && (
              <>
        
                
                {/* Actual dropdown with absolute positioning like rating dropdown */}
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  background: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                  zIndex: 999999,
                  maxHeight: '200px',
                  overflowY: 'auto',
                  marginTop: getDropdownMargin(),
                  minWidth: '150px',
                  display: 'block !important',
                  visibility: 'visible !important',
                  opacity: 1,
                  transform: 'none',
                  clip: 'auto'
                }}>
                  <div 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDropdownSelect('cuisine', '');
                    }}
                    style={{
                      padding: '0.6rem 0.75rem',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      color: '#333',
                      borderBottom: '1px solid #f0f0f0',
                      background: 'white',
                      display: 'block'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                  >
                    {t('restaurants.filters.all_cuisines')}
                  </div>
                  {availableCuisines.map((cuisine) => (
                    <div 
                      key={cuisine}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDropdownSelect('cuisine', cuisine);
                      }}
                      style={{
                        padding: '0.6rem 0.75rem',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        color: '#333',
                        borderBottom: '1px solid #f0f0f0',
                        background: 'white',
                        display: 'block'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      {cuisine}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          
          {/* Price Filter */}
          <div 
            className={`filter-pill price ${priceFilter ? 'active' : ''} ${activeDropdown === 'price' ? 'dropdown-open' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              toggleDropdown('price');
            }}
            style={{ position: 'relative' }}
          >
            <span>💰 {priceFilter ? formatPriceRangeDisplay(priceFilter) : t('restaurants.filters.all_prices')}</span>
            <span className="dropdown-arrow">▼</span>
            {activeDropdown === 'price' && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                background: 'white',
                border: '1px solid #ddd',
                borderRadius: '8px',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                zIndex: 999999,
                maxHeight: '200px',
                overflowY: 'auto',
                marginTop: getDropdownMargin(),
                minWidth: '150px',
                display: 'block',
                visibility: 'visible'
              }}>
                <div 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDropdownSelect('price', '');
                  }}
                  style={{
                    padding: '0.6rem 0.75rem',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    color: '#333',
                    borderBottom: '1px solid #f0f0f0',
                    background: 'white',
                    display: 'block'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                >
                  {t('restaurants.filters.all_prices')}
                </div>
                {availablePriceRanges.map(priceRange => (
                  <div 
                    key={priceRange}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDropdownSelect('price', priceRange);
                    }}
                    style={{
                      padding: '0.6rem 0.75rem',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      color: '#333',
                      borderBottom: '1px solid #f0f0f0',
                      background: 'white',
                      display: 'block'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                  >
                    {formatPriceRangeDisplay(priceRange)}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Rating Filter */}
          <div 
            className={`filter-pill rating ${ratingFilter ? 'active' : ''} ${activeDropdown === 'rating' ? 'dropdown-open' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              toggleDropdown('rating');
            }}
            style={{ position: 'relative' }}
            id="rating-filter-pill"
          >
            <span>⭐ {ratingFilter ? `${ratingFilter} ${t('restaurants.filters.stars')}` : t('restaurants.filters.all_ratings')}</span>
            <span className="dropdown-arrow">▼</span>
            {activeDropdown === 'rating' && (
              <>
                {/* Remove test element */}
                
                {/* Actual dropdown with proper positioning */}
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  background: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                  zIndex: 999999,
                  maxHeight: '200px',
                  overflowY: 'auto',
                  marginTop: getDropdownMargin(),
                  minWidth: '150px',
                  display: 'block',
                  visibility: 'visible'
                }}>
                  <div 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDropdownSelect('rating', '');
                    }}
                    style={{
                      padding: '0.6rem 0.75rem',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      color: '#333',
                      borderBottom: '1px solid #f0f0f0',
                      background: 'white',
                      display: 'block'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                  >
                    {t('restaurants.filters.all_ratings')}
                  </div>
                  {availableRatings.map(rating => {
                    return (
                      <div 
                        key={rating}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDropdownSelect('rating', rating);
                        }}
                        style={{
                          padding: '0.6rem 0.75rem',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          color: '#333',
                          borderBottom: '1px solid #f0f0f0',
                          background: 'white',
                          display: 'block'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                      >
                        ⭐ {rating} {t('restaurants.filters.stars')}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      

      {/* Restaurant List */}
      
      {loading ? (
        <div className="restaurant-grid">
          {Array.from({ length: 6 }, (_, index) => (
            <RestaurantShimmer key={`shimmer-${index}`} />
          ))}
        </div>
      ) : filteredRestaurants.length === 0 ? (
        <div className="empty-state">
          <p>{t('restaurants.filters.no_restaurants_found')}</p>
          <button onClick={() => {
            setSearchTerm(''); 
            setCuisineFilter(''); 
            setPriceFilter('');
            setRatingFilter('');
            setTimeFilter(false);
            setStatusFilter('');
            setDeliveryFeeFilter('');
          }}>
            {t('restaurants.filters.clear_all_filters')}
          </button>
        </div>
      ) : (
        <div className="restaurant-grid">
          {filteredRestaurants.map((restaurant) => {
                         const deliveryInfo = getDeliveryInfo(restaurant);
             const reviewCount = getReviewCount(restaurant);
             const rating = restaurant.rating ? Number(restaurant.rating).toFixed(1) : t('restaurants.filters.new');
             const restaurantImage = getRestaurantImage(restaurant);
             const restaurantStatus = getRestaurantStatus(restaurant);
             const estimatedDeliveryFee = estimatedDeliveryFees[restaurant._id] || 'N/A';
            
            return (
              <div 
                key={getStringKey(restaurant._id, `restaurant-${restaurant.name.slice(0,3)}`)} 
                className="restaurant-card-modern"
                onClick={() => handleSelectRestaurant(restaurant)}
              >
                <div className="card-image-container">
                  {restaurantImage ? (
                    <ImageComponent 
                      src={restaurantImage} 
                      alt={restaurant.name}
                      className="card-image"
                    />
                  ) : (
                    <div className="card-image-placeholder">
                      <span>{restaurant.name.charAt(0)}</span>
                    </div>
                  )}
                  
                </div>
                
                <div className="card-content">
                  <h3 className="restaurant-name-modern">{restaurant.name}</h3>
                  
                   <div className="rating-row">
                     <div className="rating-info">
                       <span className="star">⭐</span>
                       <span className="rating-number">{rating}</span>
                       <span className="rating-count">({reviewCount})</span>
                     </div>
                     <div className={`restaurant-status ${restaurantStatus}`}>
                       {restaurantStatus === 'open' ? t('restaurants.filters.open_now') : 
                        restaurantStatus === 'closing-soon' ? t('restaurants.filters.closing_soon') : t('restaurants.closed')}
                     </div>
                   </div>
                   
                   <div className="distance-time-row">
                     <div className="delivery-info">
                       <span>{deliveryInfo.distance}</span>
                       <span className="separator">•</span>
                       <span>{deliveryInfo.time}</span>
                     </div>
                   </div> 
                  
                                                       {userAddress?.coordinates && (
                    <div className="delivery-fee-row">
                      <span className="delivery-fee">
                        {isCalculatingFees ? t('restaurants.filters.calculating_delivery_fee') : 
                         estimatedDeliveryFee !== 'N/A' ? `${t('restaurants.filters.delivery_fee')}: ${estimatedDeliveryFee} ${currency}` : 
                         t('restaurants.filters.delivery_fee_unavailable')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RestaurantSelector; 